import { QcHeader } from "@/types";
import { UON, headers, operator, setOtherFilters } from "@/utils";
import { api } from "@/auth/api";
import { settings } from "@/settings";
import { useApi } from "@/requests/useApi";

// UON.SystemUid must NOT be read at module top level - this module gets
// pulled into the circular import chain (utils -> store -> translationModule
// -> this file -> utils) before UON finishes initializing there, which
// throws "Cannot access 'UON' before initialization". Every accessor below
// reads UON.SystemUid.odmConfiguration lazily instead (same as userService.ts).
const TRANSLATE_PATH = "translation/translate";
const UPLOAD_PATH = "translation/upload";

export const CONFIGURATION_TRANSLATION_TABLE = "configuration_translation";

/**
 * Registered in C:\lot1\odm-dataservice\src\main\resources\queries\translation-value-by-key.xml,
 * already live on dev. Selects configuration_translation.{id, key, value,
 * language_id}, aliased in the JSON response as {id, keyName, valueName,
 * language_id} - see translationList()'s row shape below.
 */
export const TRANSLATION_QUERY_ID = "translation-value-by-key";

/**
 * List/search translations via the shared QC dataservice query, same
 * pattern as userService.ts's userList(). Returns raw rows shaped
 * {id, keyName, valueName, language_id} per the XML's field aliases -
 * NOT the {resourceUid, key, value, ...} VO shape used by the single-row
 * REST endpoints below. TranslationList.vue normalizes this on the way in.
 */
export const translationList = async (filter: any, offset?: number, limit?: number) => {
  const operators = [];

  const qc_header: QcHeader = {
    qc: {
      queryId: TRANSLATION_QUERY_ID,
      offset: offset || 0,
      limit: limit || 1000,
      parameters: {
        ...filter,
      },
    },
  };

  if (filter?.key) {
    operators.push(
      operator({
        val: filter.key,
        table: CONFIGURATION_TRANSLATION_TABLE,
        // The XML query's name="key" is an ORM-mapped field name, resolved
        // internally for SELECT. The raw physical column is "_key" (confirmed
        // by "Unknown column 'configuration_translation.value'" when this
        // was "value" - otherFilters hits the table directly, bypassing
        // the ORM mapping).
        dbField: "_key",
      }).LIKE
    );
  }

  if (filter?.value) {
    operators.push(
      operator({
        val: filter.value,
        table: CONFIGURATION_TRANSLATION_TABLE,
        dbField: "_value",
      }).LIKE
    );
  }

  if (filter?.language) {
    operators.push(
      operator({
        val: filter.language,
        table: CONFIGURATION_TRANSLATION_TABLE,
        dbField: "language_id",
      }).EQUAL
    );
  }

  setOtherFilters(operators, qc_header);

  return await api().get(settings.api_query_url, {
    headers: headers({
      qc: JSON.stringify(qc_header.qc),
    }),
  });
};

/**
 * Maps the TranslationVO shape returned by the single-row REST endpoints
 * (translate/translate/{resourceUid}/) onto the flat row shape the rest of
 * the app (table columns, edit form) already works with.
 */
function mapVoToRow(vo: any) {
  return {
    id: vo.resourceUid,
    _key: vo.key,
    _value: vo.value,
    language_id: vo.language?.resourceUid || "",
    _daaq: vo.daaq || "/",
    _business_data: vo.businessData ?? null,
    _when_created: vo._when_created || "",
    _when_modified: vo._when_modified || "",
    _who_created: vo._who_created || "",
    _who_modified: vo._who_modified || "",
  };
}

/** GET /odm-configuration/api/1/odm-configuration/translation/translate/{resourceUid}/ */
export async function getTranslation(resourceUid: string) {
  const { response, error } = await useApi(UON.SystemUid.odmConfiguration, TRANSLATE_PATH).get(resourceUid);
  if (error) {
    throw error;
  }
  return mapVoToRow(response);
}

/** PUT /odm-configuration/api/1/odm-configuration/translation/translate/{resourceUid}/ */
export async function updateTranslation(row: any) {
  const payload: any = {
    resourceUid: row.id,
    key: row._key,
    value: row._value,
    daaq: row._daaq || "/",
  };
  if (row.language_id) {
    payload.language = { resourceUid: row.language_id };
  }

  // useApi().upsert() PUTs to this exact path whenever resourceUid is set -
  // safe here since this function is only ever called in edit mode.
  const { response, error } = await useApi(UON.SystemUid.odmConfiguration, TRANSLATE_PATH).upsert(payload);
  if (error) {
    throw error;
  }
  return response;
}

/**
 * No dedicated create endpoint exists yet (only get/put/delete were added).
 * Reuses the existing, already-working upload endpoint - it upserts by
 * (language, country, key), so it doubles as "create" here. It does not
 * return a resourceUid for the created row.
 *
 * Country is deliberately never sent (no Pays field in the form) - always
 * passing a bare language-only locale keeps every upload for a given key
 * hitting the same (language, key) match server-side, instead of silently
 * creating a second row when country was set inconsistently between calls.
 */
export async function createTranslation(row: any) {
  const odmConfiguration = UON.SystemUid.odmConfiguration;
  return await api().post(
    `${settings.api_url}/${odmConfiguration}/api/1/${odmConfiguration}/${UPLOAD_PATH}/`,
    {
      locale: row.language_id,
      content: { [row._key]: row._value },
    }
  );
}

/** DELETE /odm-configuration/api/1/odm-configuration/translation/translate/{resourceUid}/ */
export async function deleteTranslation(resourceUid: string) {
  const { response, error } = await useApi(UON.SystemUid.odmConfiguration, TRANSLATE_PATH).remove(resourceUid);
  if (error) {
    throw error;
  }
  return response;
}
